<template>
  <div>
    <section v-if="isRequestingModifiers">
      <spinner-loader :loading="isRequestingModifiers" />
    </section>

    <section v-else-if="taxesAndFees.length < 1">
      <p>
        You don't have any modifiers in your inventory yet. <br />Create your
        first one by clicking "New Modifier."
      </p>
      <b-button v-b-modal.modifierModal style="margin-top: 4px"
        >New Tax</b-button
      >
    </section>

    <section v-else>
      <div>
        <b-row class="search-header" align-h="center">
          <b-col md="6" class="my-1">
            <b-input-group>
              <b-form-input v-model="filter" :placeholder="$t('search')" />
            </b-input-group>
          </b-col>
          <b-col md="2">
            <b-button v-b-modal.modifierModal style="margin-top: 4px"
              >New Tax</b-button
            >
          </b-col>
        </b-row>
      </div>
      <b-table
        ref="modsTable"
        v-if="!isLoading"
        :items="taxesAndFees"
        :fields="fields"
        :filter="filter"
        hover
        striped
        @row-clicked="rowClicked"
        class="modifier-table"
        fixed
      >
      </b-table>

      <div v-else class="fixed-center">
        <spinner-loader />
      </div>
    </section>
    <modifier-modal
      ref="modifierModal"
      @submit="submitModifier"
      @delete="deleteModifier"
    />
  </div>
</template>

<script>
import SpinnerLoader from "@/components/widgets/SpinnerLoader";
import ModifierModal from "@/components/modals/ModifierModal";
import cloneDeep from "lodash.clonedeep";
import { mapGetters, mapState, mapActions } from "vuex";
import { MODIFIER_RATE_TYPES, MODIFIER_TYPES } from "@/config/constants";

export default {
  name: "TaxesAndFees",
  async mounted() {
    this.isLoading = true;
    await this.getTaxes();
    this.isLoading = false;
  },
  computed: {
    ...mapState({
      isRequestingModifiers: (state) => state.modifiers.isRequestingModifiers,
    }),
    ...mapGetters(["getAvailableModifiers"]),
    taxesAndFees() {
      return this.getAvailableModifiers.filter(
        (modifier) =>
          modifier.category_id === MODIFIER_TYPES.TAX ||
          modifier.category_id === MODIFIER_TYPES.FEE
      );
    },
  },
  data() {
    return {
      modifiers: [],
      fields: [
        {
          key: "name",
        },
        {
          key: "description",
        },
        {
          key: "default_rate",
          formatter: (value, field, row) => {
            const formattedValue = parseFloat(value).toFixed(2);
            if (row.modifier_type_id === MODIFIER_RATE_TYPES.PERCENTAGE)
              return `${formattedValue}%`;
            if (row.modifier_type_id === MODIFIER_RATE_TYPES.FIXED)
              return `$${formattedValue}`;
            else return formattedValue;
          },
        },
        {
          key: "category_id",
          label: "Type",
          formatter: (value) => {
            if (value === MODIFIER_TYPES.TAX) return "Tax";
            if (value === MODIFIER_TYPES.FEE) return "Fee";
            else return "UNKNOWN";
          },
        },
        {
          label: "Calculation",
          key: "modifier_type_id",
          formatter: (value) => {
            if (value === MODIFIER_RATE_TYPES.FIXED) return "Fixed";
            if (value === MODIFIER_RATE_TYPES.PERCENTAGE) return "Percentage";
            else return "UNKNOWN";
          },
        },
      ],
      filter: "",
      isLoading: false,
      selectedModifier: null,
    };
  },
  methods: {
    ...mapActions([
      "getTaxes",
      "createModifier",
      "updateModifier",
      "createModifierTag",
    ]),

    rowClicked(modifier) {
      this.$refs.modifierModal.open(modifier);
    },
    async submitModifier(submittedModifier) {
      const modifierTags = cloneDeep(submittedModifier.tax_tag);
      delete submittedModifier.tax_tag;

      const requestType = submittedModifier.id
        ? "updateModifier"
        : "createModifier";

      const savedModifier = await this[requestType](submittedModifier);

      if (requestType === "createModifier") {
        const newModifierTags = await this.createModifierTags(
          savedModifier,
          modifierTags
        );
      }

      this.$refs.modsTable.refresh();
    },
    deleteModifier() {
      this.$refs.modsTable.refresh();
    },
    createModifierTags(modifier, tags) {
      Promise.all(
        tags.map((tag) => {
          return this.createModifierTag({
            tag_id: tag.id,
            modifier_id: modifier.id,
          });
        })
      );
    },
  },
  components: {
    ModifierModal,
    SpinnerLoader,
  },
};
</script>

<style scoped lang="scss"></style>
